import { Link } from "react-router-dom";
import { Scale, FileText, Shield, AlertTriangle, BookOpen, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { Separator } from "../components/ui/separator";

export default function AlabamaLawPage() {
  return (
    <div data-testid="alabama-law-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Legal Requirements</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-4xl">
            Alabama Church School Legal Requirements
          </h1>
          <p className="mt-6 text-base text-white/60 max-w-2xl leading-relaxed">
            Understanding Alabama's church school law before you start your school.
          </p>
        </div>
      </section>

      {/* Notice Banner */}
      <section className="bg-[#C8A97E]/10 border-b border-[#C8A97E]/20 py-4">
        <div className="max-w-3xl mx-auto px-6 md:px-12 flex items-start gap-3">
          <Info className="w-5 h-5 text-[#C8A97E] shrink-0 mt-0.5" />
          <p className="text-sm text-[#595959] leading-relaxed">
            This page provides general educational information about Alabama church-school law. It is <strong className="text-[#1C2E4A]">not legal advice</strong> and should not be used as a substitute for advice from a licensed Alabama attorney.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">

          {/* Understanding Alabama's Church School Law */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#C8A97E]" />
              Understanding Alabama's Church School Law
            </h2>
            <p className="text-sm text-[#595959] leading-relaxed">
              Alabama law recognizes church schools as a distinct type of nonpublic school. Church schools are not public schools and are not treated the same as ordinary private schools for many state registration, reporting, licensing, and regulatory requirements.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* What Is an Alabama Church School? */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">What Is an Alabama Church School?</h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              Under <strong className="text-[#1C2E4A]">Alabama Code &sect; 16-28-1</strong>, a church school is a school that:
            </p>
            <ul className="space-y-2.5 mb-4">
              {[
                "Offers instruction in grades K\u201312, or any combination thereof, including the kindergarten, elementary, or secondary level.",
                "May provide instruction through on-site programs, home programs, or both.",
                "Operates as a ministry of a local church, group of churches, denomination, and/or association of churches on a nonprofit basis.",
                "Does not receive any state or federal funding.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#595959]">
                  <CheckCircle2 className="w-4 h-4 text-[#8F9779] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-[#595959] leading-relaxed">
              A school must satisfy this legal definition to claim the exemptions Alabama law provides to church schools.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Homeschooling & Private Tutor Alternative */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">Homeschooling in Alabama</h2>
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-4">
              <p className="text-sm font-medium text-[#1C2E4A] mb-3">Private Tutor Requirements (Alabama Code &sect; 16-28-5)</p>
              <p className="text-sm text-[#595959] leading-relaxed mb-3">
                Individuals who choose to homeschool and <em>do not</em> enroll through a church school fall under the option of school attendance through a private tutor. Instruction by a private tutor means only instruction by a person who:
              </p>
              <ul className="space-y-2 mb-3">
                {[
                  "Holds a certificate issued by the state superintendent of education.",
                  "Offers instruction in the several branches of study required to be taught in the public schools of Alabama.",
                  "Provides at least three hours a day of instruction for 140 days each calendar year, between the hours of 8:00 A.M. and 4:00 P.M.",
                  "Uses the English language in giving instruction.",
                  "Keeps a register of work, showing daily the hours used for instruction and the presence or absence of any child being instructed.",
                  "Makes such reports as the State Board of Education may require.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#595959]">
                    <span className="text-[#C8A97E] font-bold shrink-0">&bull;</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#595959]/70 leading-relaxed">
                Prior to beginning instruction, the tutor must file a statement with the county or city superintendent of education showing the child or children to be instructed, the subjects to be taught, and the period of time such instruction is proposed to be given.
              </p>
            </div>
            <p className="text-sm text-[#595959] leading-relaxed">
              The church school option provides significantly more flexibility than the private tutor option, which is why many Alabama homeschool families choose to enroll through a church school.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Enrollment Notice */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C8A97E]" />
              Church School Enrollment Notice
            </h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              When a child enrolls in a church school, the child's parent or guardian must file an enrollment and attendance notice with the local public-school superintendent (<strong className="text-[#1C2E4A]">Alabama Code &sect; 16-28-7</strong>).
            </p>
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 mb-4">
              <p className="text-sm font-medium text-[#1C2E4A] mb-3">The process generally includes:</p>
              <div className="space-y-3">
                {[
                  "The church school provides the parent with the required enrollment form.",
                  "The church-school administrator countersigns the form to confirm the child's enrollment.",
                  "The parent returns the completed form to the local public-school superintendent or the superintendent's designated office.",
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1C2E4A] text-white flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</span>
                    <p className="text-sm text-[#595959] leading-relaxed pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-[#595959]/70 leading-relaxed">
              Although the form may be delivered to an office commonly called the local board of education, the law identifies the local public-school superintendent as the official with whom the notice is filed.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* When a Student Leaves */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">When a Student Leaves the Church School</h2>
            <p className="text-sm text-[#595959] leading-relaxed">
              At enrollment, the parent authorizes the church school to notify the local public-school superintendent if the child stops attending or is no longer enrolled in the church school.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed mt-3">
              Church schools should therefore maintain accurate enrollment and withdrawal records and provide the required notice when a student's enrollment ends.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Exemptions */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C8A97E]" />
              Requirements That Generally Do Not Apply to Church Schools
            </h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              Alabama church schools are generally exempt from many requirements imposed on public schools and certain private schools. Alabama education law does not generally require a church school to:
            </p>
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
              <ul className="space-y-2">
                {[
                  "Register annually with the Alabama State Department of Education.",
                  "Obtain a state license to operate.",
                  "Obtain accreditation.",
                  "Employ state-certified teachers.",
                  "Use a state-selected or state-approved curriculum.",
                  "Submit weekly absence reports to the local superintendent.",
                  "Submit annual enrollment, staffing, tuition, property, or curriculum reports to the State Department of Education.",
                  "Follow public-school standardized-testing requirements.",
                  "Follow public-school instructional-hour requirements.",
                  "Operate according to the public-school calendar.",
                  "Submit its educational program to state or local school officials for approval.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#595959]">
                    <span className="text-[#8F9779] font-bold shrink-0">&mdash;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-[#595959] leading-relaxed mt-4">
              A church school may voluntarily adopt its own academic standards, attendance policies, graduation requirements, recordkeeping procedures, teacher qualifications, curriculum requirements, and student policies.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Attendance */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">Attendance and Family Responsibilities</h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-3">
              Alabama's compulsory-school-attendance law generally applies to children between <strong className="text-[#1C2E4A]">6 and 17 years of age</strong>.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed mb-3">
              The parent or guardian is responsible for ensuring that a school-age child is enrolled in a lawful educational option and for filing the required church-school enrollment notice with the local public-school superintendent.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed">
              Parents are also responsible for following the church school's policies regarding attendance, academic progress, records, curriculum, grades, withdrawal, and graduation.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Church School Records */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#C8A97E]" />
              Church School Records
            </h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              Although Alabama law does not impose the same state-reporting system used for public schools, a responsibly operated church school should maintain records necessary to document its relationship with enrolled families. These may include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                "Enrollment applications",
                "Countersigned enrollment forms",
                "Enrollment dates",
                "Withdrawal dates",
                "Parent authorization to report withdrawal",
                "Copies of withdrawal notifications",
                "Attendance records",
                "Academic records for families",
                "Transcripts, report cards, diplomas",
                "School policies and enrollment agreements",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[#595959]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#8F9779] shrink-0 mt-0.5" />
                  {item}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#595959]/70 leading-relaxed mt-4">
              Some of these records may be maintained as part of the church school's own policies rather than because Alabama law expressly requires every listed document.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Church School Autonomy */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">Church School Autonomy</h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-3">
              Alabama law recognizes the right of parents to select public, private, religious, nonreligious, church-school, or home-based education.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed mb-3">
              Nonpublic schools, including church and religious schools, are generally not subject to licensing or regulation by the Alabama State Department of Education or local public-school authorities.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed">
              The local public-school superintendent's receipt of an enrollment form does not make the church school part of the public-school system and does not give the superintendent authority to manage the church school's curriculum, instructional methods, teachers, records, policies, or daily operations.
            </p>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Important Distinction */}
          <div className="mb-12">
            <div className="bg-[#1C2E4A]/5 rounded-xl border border-[#E5E2DC] p-6">
              <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#C8A97E]" />
                Important Distinction
              </h2>
              <p className="text-sm text-[#595959] leading-relaxed mb-3">
                The local school board and the local superintendent are <strong className="text-[#1C2E4A]">not the same entity</strong>.
              </p>
              <p className="text-sm text-[#595959] leading-relaxed mb-3">
                The school board is the governing body responsible for overseeing the local public-school system. The superintendent is the administrator responsible for operating that system and receiving certain notices required under Alabama's compulsory-attendance laws.
              </p>
              <p className="text-sm text-[#595959] leading-relaxed">
                Church-school enrollment paperwork is filed with the local public-school superintendent, even when it is delivered through an office labeled as the board of education.
              </p>
            </div>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Alabama Code Sections */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-4">Alabama Code Sections</h2>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              The principal Alabama statutes relating to church schools include:
            </p>
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 space-y-2.5">
              {[
                { code: "Alabama Code § 16-28-1", desc: "Definition of a church school." },
                { code: "Alabama Code § 16-28-3", desc: "Compulsory-school-attendance ages and church-school enrollment compliance." },
                { code: "Alabama Code § 16-28-7", desc: "Enrollment notice, administrator countersignature, and withdrawal notification." },
                { code: "Alabama Code § 16-28-8", desc: "Exemption from certain required reports." },
                { code: "Alabama Code § 16-1-11", desc: "Exemption from annual private-school registration and statistical reporting." },
                { code: "Alabama Code § 16-1-11.1", desc: "Legislative findings concerning nonpublic-school autonomy." },
                { code: "Alabama Code § 16-1-11.2", desc: "Exemption of nonpublic schools from state licensure and regulation." },
                { code: "Alabama Code § 16-28-24", desc: "Voluntary waiver of certain church-school exemptions." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="font-semibold text-[#1C2E4A] shrink-0 min-w-[220px]">{item.code}:</span>
                  <span className="text-[#595959]">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <Separator className="my-10 bg-[#E5E2DC]" />

          {/* Legal Disclaimer */}
          <div className="mb-12">
            <div className="bg-[#C8A97E]/5 rounded-xl border border-[#C8A97E]/20 p-6">
              <h2 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-3">Legal Disclaimer</h2>
              <p className="text-sm text-[#595959] leading-relaxed mb-3">
                This information is provided for general educational purposes only. It does not constitute legal advice, create an attorney-client relationship, or guarantee compliance with every law that may apply to a particular church, ministry, business, school, employee, property, or situation.
              </p>
              <p className="text-sm text-[#595959] leading-relaxed">
                Church schools may also be subject to laws outside Alabama's education statutes, including generally applicable laws involving business formation, contracts, taxes, employment, child safety, privacy, nondiscrimination, zoning, insurance, and building or fire safety. Anyone establishing or operating a church school should consult a qualified Alabama attorney and tax professional regarding their particular organization.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#1C2E4A] rounded-xl p-8 text-center">
            <h3 className="font-serif text-xl font-semibold text-white mb-3">Ready to Start Your Church School?</h3>
            <p className="text-sm text-white/60 mb-6 max-w-lg mx-auto">
              ACS Virtual Church builds complete church school systems so you can focus on your ministry and families.
            </p>
            <Link
              to="/start-a-church-school"
              data-testid="law-page-cta"
              className="inline-flex items-center gap-2 bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md transition-colors font-medium"
            >
              Start a Church School <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
      </section>
    </div>
  );
}
